import { currentDocId, getNow, plugins, projectStore } from '@dabble/app';
import { hasChanges } from '@dabble/data/collective/changes';
import { Signal, signal } from '@dabble/data/signals';
import { locallyStoredWritable } from '@dabble/data/stores/locally-stored-writable';
import { ProjectStore } from '@dabble/data/stores/project';
import { derived, Readable } from '@dabble/data/stores/store';
import { Doc } from '@dabble/data/types';
import { createId } from '@dabble/data/uuid';
import { getImmutableValue, makeChanges } from '@dabble/util/immutable';

const EMPTY: StickyNoteMap = {};
export const SIZE = 240;

export interface StickyNote {
  id: string;
  docId: string;
  top: number;
  horizontal: number;
  content: string;
  created: number;
}

export interface CreateStickyNotes {
  docId: string;
  top: number;
  horizontal: number;
  content: string;
}

export interface StickyNoteMap {
  [id: string]: StickyNote;
}

export interface StickyNotesStore extends Readable<StickyNoteMap> {
  changing: Signal;
  createStickyNote(data: CreateStickyNotes): Promise<string>;
  updateStickyNote(id: string, updates: Partial<StickyNote>): Promise<void>;
  deleteStickyNote(id: string): Promise<void>;
}

export const stickyNotesStore = createStickyNotesStores(projectStore, currentDocId);
export const showStickyNotes = locallyStoredWritable('showStickyNotes', true);
plugins.register({ stickyNotes: stickyNotesStore, showStickyNotes });

export function getBoundedPosition(top: number, left: number, height: number, width: number) {
  const horizontal = Math.max(0, Math.min(1, left / (width - SIZE)));
  top = Math.round(Math.max(0, Math.min(height - SIZE, top)));
  return { top, horizontal };
}

// Holds all the stickyNotes for currently displayed editors and their position
export function createStickyNotesStores(projectStore: ProjectStore, currentDocId: Readable<string>): StickyNotesStore {
  const changing = signal();
  let stickyNotes: StickyNoteMap = EMPTY;
  let lastDocId: string;

  // This store is a map of all stickyNotes by id in the current doc and all of its decendents (including virtual docs)
  const { get, subscribe } = derived([projectStore, currentDocId], ([{ docs, childrenLookup }, currentDocId]) => {
    if (currentDocId !== lastDocId) {
      changing();
      lastDocId = currentDocId;
    }
    if (!currentDocId) {
      return (stickyNotes = EMPTY);
    }
    const ids = new Set(Object.keys(stickyNotes));

    makeChanges(() => {
      addStickyNotes(docs[currentDocId]);
      if (ids.size) {
        stickyNotes = getImmutableValue(stickyNotes);
        ids.forEach(id => delete stickyNotes[id]);
      }

      function addStickyNotes(doc: Doc) {
        if (!doc) return;
        if (doc.stickyNotes) {
          const fields = new Set<string>();
          Object.values(doc.stickyNotes as StickyNoteMap).forEach(stickyNote => {
            ids.delete(stickyNote.id);
            if (stickyNotes[stickyNote.id] !== stickyNote) {
              // Doing it this way will make stickyNotes not change if no stickyNotes were updated in the project
              stickyNotes = getImmutableValue(stickyNotes);
              stickyNotes[stickyNote.id] = stickyNote;
            }
          });
        }
        childrenLookup[doc.id]?.forEach(addStickyNotes);
      }
    });

    return stickyNotes;
  });

  async function createStickyNote(data: CreateStickyNotes) {
    const { docId } = data;
    const doc = projectStore.getDoc(docId);

    const patch = projectStore.patch();
    if (!doc.stickyNotes) {
      patch.patch.add(`/docs/${docId}/stickyNotes`, {});
    }
    const stickyNote: StickyNote = {
      ...data,
      id: createId(4, doc.stickyNotes, docId),
      created: getNow(),
    };
    patch.patch.add(`/docs/${docId}/stickyNotes/${stickyNote.id}`, stickyNote);
    await patch.save();
    return stickyNote.id;
  }

  async function updateStickyNote(id: string, updates: Partial<StickyNote>) {
    const { stickyNote, path } = findStickyNote(id);
    if (!stickyNote) return;
    if (!hasChanges(stickyNote, updates)) return;
    const patch = projectStore.patch();
    patch.patch.addUpdates(updates, path);
    await patch.save();
  }

  async function deleteStickyNote(id: string) {
    const { stickyNote, path } = findStickyNote(id);
    if (!stickyNote) return;
    const patch = projectStore.patch();
    patch.patch.remove(path);
    await patch.save();
  }

  function findStickyNote(id: string) {
    const stickyNote = stickyNotes[id];
    const docId = stickyNote?.docId;
    const path = `/docs/${docId}/stickyNotes/${id}`;
    return { stickyNote, path, docId };
  }

  return {
    changing,
    createStickyNote,
    updateStickyNote,
    deleteStickyNote,
    get,
    subscribe,
  };
}
